import { useDispatch, useSelector } from 'react-redux';
import { PARENT_EVENTS, SOlAR_QOUTE_APP_NAME } from '../constants';
import { createContext, ReactNode, useContext, useEffect, useLayoutEffect, useReducer } from 'react';
import { AppDispatch, RootState } from 'store';
import { setDisplayMode } from 'store/slices/QueryParamsSlice';

type Theme = {
	colors: { [key: string]: string };
	runAiBtnText: string;
	selectHouseText: string;
	saveDesignBtnText: string;
	adjustPanelText: string;
	mapMarkerColor: string;
	nextSteps?: Array<{
		step: number;
		title: string;
		desc: string;
	}>
	showNextSteps: boolean;
	adjustSystemSizeText: string | null;
	mainHeading: string;
}

const defaultThemeColors: { [key: string]: string } = {
	// primary: '#003cff',
	// secondary: '#ffc005',
	// defaultBgColor: '#fff',
	// defaultBgLightColor: '#F5F7FF',
	// textBodyColor: '#2e384d',
	// textTitleColor: '#64686a',
	// textHeadingColor:'#223349',
	// subHeadingColor: '#4a525a',
	// borderColor: '#d6d6d6',
	// activeBgColor: '#eceff4',
	// defaultIconColor: '#64686A',

	// Brand
	primary: '#003CFF',
	secondary: '#FEB517',
	// Middle ring of the dark-mode loader spinner (see Loader/index.tsx) — pixel-sampled from
	// loader2.gif, not derived from primary/secondary; it's its own distinct navy tone there.
	loaderAccentColor: '#0027A8',

	// Backgrounds
	defaultBgColor: '#FFFFFF',
	defaultBgLightColor: '#F5F7FF',
	surfaceColor: '#f7f8fa',
	surfaceLightColor: '#F4F5FB',
	inputBgColor: '#FFFFFF',
	hoverBgColor: '#F5F7FA',
	activeBgColor: '#ECEFF4',
	
	// Text
	textColor: '#2E384D',
	textTitleColor: '#4A525A',
	textHeadingColor: '#223349',
	subHeadingColor: '#64686A',
	// textMutedColor: '#7A828A',
	// textDisabledColor: '#A5A9AE',
	btnLabel: '#fff',
	secondaryTextColor: '#666666',

	//Button
	btnBgdefault: '#FFFFFF',
	btnBglightblue: '#f4f5fb',
	btnBgPrimary: '#003CFF',
	btnBgSecondary: '#FEB517',
	btnBadge: '#f4f5ff',
	badgeLight: '#f4f5fb',
	badgeGary:'#f4f5ff',
	btnBgNo: '#bdbdbd',

	// Borders
	borderColor: '#D6D6D6',
	borderLightColor: '#E4E4E4',

	// Icons
	defaultIconColor: '#64686A',
	iconMutedColor: '#8A9198',

	// States
	disabledBgColor: '#fafafa',
	disabledTextColor: '#E4E4E4',

	// Toolbar
	toolbarBgColor: '#F1F3F6',
	toolbarthumbColor: '#FFFFFF',
	toolbarButtonHoverColor: '#F5F5F5',
	// toolbarButtonActiveColor: '#E8EDFF',

	// Input
	inputBorderColor: '#D6D6D6',
	inputHoverBorderColor: '#B8BEC7',
	inputFocusBorderColor: '#003CFF',
	inputBGOpacity: '#FFFFFF',
	// inputDisabledBgColor: '#F5F5F5',
	// inputDisabledTextColor: '#A5A9AE',
	inputErrorBgColor: '#fff3f3',
	inputErrorBorderColor: '#f40b00',
	inputErrorTextColor: '#c5c5c5',
	colorScheme: 'light',

	// Controls
	controlBgColor: '#FFFFFF',
	controlHoverBgColor: '#F5F5F5',
	controlActiveBgColor: '#E8EDFF',
	controlBorderColor: '#C7CACF',
 
	// Toggle
	toggleTrackColor: '#dadbdc',
	toggleTrackDark: '#bdbdbd',

	// toggleActiveTrackColor: '#003CFF',
	toggleThumbColor: '#FFFFFF',

	// Floating / Overlay
	// floatingBgColor: '#FFFFFF',
	// floatingBorderColor: '#D6D6D6',

	// Canvas / Map
	// canvasBgColor: '#E8EEF1',
	// gridColor: '#C9D3D8',

	// Drawing / Facet
	// facetColor: '#003CFF',
	// facetActiveColor: '#0035E6',
	// facetOutlineColor: '#6B7F91',
	// selectionColor: '#003CFF',

	// Select / Dropdown
	selectDropdownBgColor: '#fff',
	selectDropdownHoverColor: '#eef2fa',
	selectDropdownActiveColor: '#dce3f7',
	selectDropdownDisabledBgColor: '#f2f4f7',
	selectDropdownDisabledTextColor: '#9ca3af',

	// Modal
	modalBgColor: '#ffffff',
	modalBorderColor: '#d7d7d7',
	modalSidebarBG: '#f7f8fa',

	// Sidebar
	sidebarBgColor: '#f7f8fa',

	// Card
	// cardBgColor: '#2A2F38',

	// Other
	darkAccentColor: '#28373E',
	labelValueColor: '#28373E',
	warningColor: '#c53030',

	lightBlue: '#eceff4',

	// Canvas grid behind the imagery (2D editor)
	canvasGridBg: 'url(bg3dsq.png)',

	// Cards / chips (tree panel, previews)
	cardBgColor: '#F8F9FF',
	cardBorderColor: '#DCDFF1',
	chipBgColor: '#f3f4f6',
	selectedBgColor: '#E8F0FF',
	facetActiveBgColor: '#E5EBFF',
	toggleGroupBgColor: '#eef2f7',
	toggleGroupBorderColor: '#e3e3e3',

	// Pitch slider (facet list)
	sliderFillColor: '#8497AE',
	sliderTrackColor: '#BCBDC6',

	// Facet list "Complete array details" block
	arrayDetailsActiveBgColor: '#e0e9ff',
	mutedLabelColor: '#6b7280',

	// Shared toolbar/strip active state (hover uses --hoverBgColor)
	toolActiveBgColor: 'rgb(0 60 255 / 10%)',

	// Adjust Shading "Complete array details" block
	shadingArrayBgColor: '#fbfbfb',
	shadingArrayBorderColor: '#eaeaea',

	// Misc surfaces / states
	iconHoverColor: '#000',
	scrollbarThumbColor: '#d3d3d3',
	scrollbarTrackColor: '#f5f5f5',
	overlayBgColor: '#ffffff8f',
	modalBtnHoverBgColor: '#c4c4c4',
	errorBgColor: '#fff1f0',
	dangerBgColor: '#FEE2E2',
	dangerHoverBgColor: '#feeeee',
	dangerTextColor: '#B91C1C',

	// Edge colour legend over the map
	legendItemBgColor: 'rgba(20, 18, 18, 0.8)',
	legendSwatchBorderColor: 'rgb(255, 255, 255)',

	// Tree preview
	treeCrownColor: '#2d5a1b',
	treeTrunkColor: '#6b3a2a',

};

const darkThemeColors: { [key: string]: string } = {
	// Tokens mirror the parent sales tool's dark theme so the iframe blends in:
	// page #121212, card #1f1f1f, panel #303030 (border #383838), controls #121212 (border #4a525a)

	// Brand
	primary: '#4f75ff',
	secondary: '#FEB517',
	// Same ratio to dark-mode primary as the light-mode loaderAccentColor is to light-mode primary.
	loaderAccentColor: '#214CA6',

	// Backgrounds
	defaultBgColor: '#1f1f1f',
	defaultBgLightColor: '#303030',
	surfaceColor: '#303030',
	surfaceLightColor: '#303030',
	inputBgColor: '#121212',
	hoverBgColor: '#2a2a2a',
	activeBgColor: '#262d42',
	
	// Text
	textColor: '#c5c5c5',
	textTitleColor: '#dbd9d9',
	textHeadingColor: '#ffffff',
	subHeadingColor: '#bbbbbb',
	btnLabel: '#FFFFFF',
	secondaryTextColor: '#bbbbbb',

	//Button
	btnBgdefault: '#121212',
	btnBglightblue: '#303030',
	btnBgPrimary: '#1e46c7',
	btnBgSecondary: '#FEB517',
	btnBadge: '#383838',
	badgeLight: '#292F3D',
	badgeGary: 'rgb(79 117 255 / 18%)',
	btnBgNo: '#4a4a4a',

	// Borders
	borderColor: '#4a525a',
	borderLightColor: '#383838',

	// Icons
	defaultIconColor: '#bbbbbb',
	iconMutedColor: '#8E969F',

	// States
	disabledBgColor: '#2a2a2a',
	disabledTextColor: '#5c6268',

	// Toolbar
	toolbarBgColor: '#303030',
	toolbarthumbColor: '#fcfcfc',
	toolbarButtonHoverColor: '#2a2a2a',
	toolbarButtonActiveColor: '#2a3a74',

	// Input
	inputBorderColor: '#4a525a',
	inputHoverBorderColor: '#5C6570',
	inputFocusBorderColor: '#4f75ff',
	inputBGOpacity: 'rgba(0, 0, 0, 0.19)',
	
	// Dark theme — input validation error
	inputErrorBgColor: '#352529',
	inputErrorBorderColor: '#E57373',
	inputErrorTextColor: '#FF8A8A',
	colorScheme: 'dark',

	// Controls
	controlBgColor: '#121212',
	controlHoverBgColor: '#2a2a2a',
	controlActiveBgColor: '#2a3a74',
	controlBorderColor: '#4a525a',

	// Toggle
	toggleTrackColor: '#4a4a4a',
	toggleTrackDark: '#4a4a4a',
	toggleThumbColor: '#ededed',

	// Select / Dropdown (opaque equivalent of rgba(0,0,0,.19) over the #303030 panel)
	selectDropdownBgColor: '#272727',
	selectDropdownHoverColor: 'rgba(255, 255, 255, 0.08)',
	selectDropdownActiveColor: '#383838',
	selectDropdownDisabledBgColor: '#1c1c1c',
	selectDropdownDisabledTextColor: '#666666',

	// Modal
	modalBgColor: '#1f1f1f',
	modalBorderColor: '#383838',
	modalSidebarBG: '#303030',

	// Sidebar
	sidebarBgColor: '#303030',

	// Other
	darkAccentColor: '#28373E',
	labelValueColor: '#dbd9d9',
	warningColor: '#E57373',

	lightBlue: '#1f1f1f',

	// Canvas grid behind the imagery (2D editor)
	canvasGridBg: 'url(bg3dsq-dark.png)',

	// Cards / chips (tree panel, previews)
	cardBgColor: '#303030',
	cardBorderColor: '#383838',
	chipBgColor: '#121212',
	selectedBgColor: '#2a3a74',
	facetActiveBgColor: '#2a3a74',
	toggleGroupBgColor: '#121212',
	toggleGroupBorderColor: '#383838',

	// Pitch slider (facet list)
	sliderFillColor: '#6d84a3',
	sliderTrackColor: '#4a4a4a',

	// Facet list "Complete array details" block (a step lighter than the selected card)
	arrayDetailsActiveBgColor: '#323c5e',
	mutedLabelColor: '#a3abb8',

	// Shared toolbar/strip active state (hover uses --hoverBgColor)
	toolActiveBgColor: 'rgb(79 117 255 / 18%)',

	// Adjust Shading "Complete array details" block
	shadingArrayBgColor: '#303030',
	shadingArrayBorderColor: '#383838',

	// Misc surfaces / states
	iconHoverColor: '#ffffff',
	scrollbarThumbColor: '#4a4a4a',
	scrollbarTrackColor: '#1f1f1f',
	overlayBgColor: 'rgba(18, 18, 18, 0.56)',
	modalBtnHoverBgColor: '#4a4a4a',
	errorBgColor: '#3a1f1f',
	dangerBgColor: '#3a1f22',
	dangerHoverBgColor: '#4a2328',
	dangerTextColor: '#f19595',

	// Edge colour legend over the map
	legendItemBgColor: 'rgba(31, 31, 31, .85)',
	legendSwatchBorderColor: '#383838',

	// Tree preview (lifted so the shapes read on the dark card)
	treeCrownColor: '#4a8a2e',
	treeTrunkColor: '#8a5038',
};

const defaultTheme = {
	colors: defaultThemeColors,
	runAiBtnText: 'Confirm',
	selectHouseText: 'Place the crosshairs over the center of the house in the map',
	saveDesignBtnText: 'Save',
	adjustPanelText: 'Adjust Panel Here',
	mapMarkerColor: 'black',
	showNextSteps: false,
	mainHeading: '',
	adjustSystemSizeText: null
};

const defaultB2CTheme = {
	...defaultTheme,
	selectHouseText: 'Select your home and put in the center of the map',
	saveDesignBtnText: 'Next',
	mapMarkerColor: 'red',
	mainHeading: 'Your Home Design'
};

type Action = { type: 'SET_THEME'; payload: { 
	settings: organization['settings'], 
	parentAppName: string; 
	config?: organization['configurations']
} };

function themeReducer(state: Theme, action: Action): Theme {
	if (action.type === 'SET_THEME') return configureTheme(state, action.payload);
	return state;

}

function configureTheme(state: Theme, payload: Action['payload']) {
	const { parentAppName, settings, config } = payload;
	const useB2CTheme = settings?.isB2C || parentAppName === SOlAR_QOUTE_APP_NAME;
	const defaultOrgTheme = useB2CTheme ? defaultB2CTheme : defaultTheme;

	return ({
		...defaultOrgTheme,
		colors: { ...state.colors, ...(config?.cssVariables || settings?.cssVariables || {})},
		runAiBtnText : config?.setHouseButtonText || settings?.dataForTool?.runAiBtnText || defaultOrgTheme.runAiBtnText,
		selectHouseText: config?.setHouseHelpText || settings?.dataForTool?.selectHouseText || defaultOrgTheme.selectHouseText,
		saveDesignBtnText: config?.saveDesignButtonText || settings?.dataForTool?.seeRecommendButtonText || defaultOrgTheme.saveDesignBtnText,
		adjustPanelText: config?.adjustPanelText || settings?.dataForTool?.adjustPanelText || defaultOrgTheme.adjustPanelText,
		adjustSystemSizeText: config?.adjustSystemSizeHelpText || settings?.dataForTool?.adjustSystemSizeText,
	});
}

const ThemeContext = createContext<Theme>(defaultTheme);
const ThemeDispatchContext = createContext<React.Dispatch<Action> | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
	const [state, dispatch] = useReducer(themeReducer, defaultTheme);
	const displayMode = useSelector((state: RootState)=>state.QueryParams.data).displayMode;
	const reduxDispatch = useDispatch<AppDispatch>();

	useEffect(() => {
		// Parent app toggles its theme at runtime:
		// iframe.contentWindow.postMessage({ id: 'DISPLAY_MODE_CHANGED', detail: { displayMode: 'dark' } }, '*')
		const handleDisplayModeMessage = (event: MessageEvent) => {
			if (event.source !== window.parent) return;
			if (event.data?.id !== PARENT_EVENTS.DISPLAY_MODE_CHANGED) return;
			const mode = event.data?.detail?.displayMode;
			if (mode !== 'light' && mode !== 'dark') return;
			reduxDispatch(setDisplayMode(mode));
		};
		window.addEventListener('message', handleDisplayModeMessage);
		return () => window.removeEventListener('message', handleDisplayModeMessage);
	}, [reduxDispatch]);

	useLayoutEffect(() => {
		const colors = displayMode === 'dark' ? darkThemeColors : state.colors;
		Object.entries(colors).forEach(([key, value]) => {
			document.documentElement.style.setProperty(`--${key}`, value);
		});
		// public/index.html paints <html> dark before the bundle loads; from here on these follow the
		// mode, so switching to light at runtime clears them (native scrollbars/selects go light again).
		const htmlStyle = document.documentElement.style;
		if (displayMode === 'dark') {
			htmlStyle.background = 'var(--defaultBgColor)';
			htmlStyle.colorScheme = 'dark';
		} else {
			htmlStyle.removeProperty('background');
			htmlStyle.removeProperty('color-scheme');
		}
	}, [displayMode, state.colors]);

	return (<ThemeContext.Provider value={state}>
		<ThemeDispatchContext.Provider value={dispatch}>
			{children}
		</ThemeDispatchContext.Provider>
	</ThemeContext.Provider>);
}

export function useOrgTheme() {
	const context = useContext(ThemeContext);
	if (!context) {
		throw new Error('useOrgTheme should be used within a ThemeProvider');
	}
	return context;
}

export function useThemeDispatch() {
	const context = useContext(ThemeDispatchContext);
	if (!context) {
		throw new Error('useOrgTheme should be used within a ThemeProvider');
	}
	return context;
}
